import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import styles from './DatePicker.module.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const POPUP_W = 260;   // .popup min-width
const POPUP_H = 300;   // enough for a 6-row month plus the nav
const GAP     = 6;     // breathing room between field and popup
const EDGE    = 8;     // keep clear of the viewport edges

function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return { year: y, month: m - 1, day: d };
}

function formatDisplay(str) {
  const p = parseDate(str);
  if (!p) return '';
  return `${MONTHS[p.month]} ${p.day}, ${p.year}`;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function DatePicker({ value, onChange, placeholder = 'Select date', className, error }) {
  const today = new Date();
  const parsed = parseDate(value);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [viewYear, setViewYear]   = useState(parsed?.year  ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.getMonth());
  const wrapRef = useRef(null);
  const popupRef = useRef(null);

  /* The popup is portalled to <body>. Inside the drawer it sat in a scrolling
     container, so it was clipped by that container's bounds — a stacking
     context problem no z-index can fix. Fixed coordinates off the trigger's
     rect put it above everything instead. */
  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    // Prefer above the field, as it has always sat; drop below when the space
    // up there can't hold it.
    const above = r.top - GAP - POPUP_H >= EDGE;
    const top = above ? r.top - GAP - POPUP_H : r.bottom + GAP;

    const maxLeft = window.innerWidth - POPUP_W - EDGE;
    setPos({ top, left: Math.max(EDGE, Math.min(r.left, maxLeft)) });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();

    function onDown(e) {
      const inTrigger = wrapRef.current?.contains(e.target);
      const inPopup = popupRef.current?.contains(e.target);
      if (!inTrigger && !inPopup) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    // Capture, so scrolling inside the drawer repositions it too — not just
    // scrolling the page.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function selectDay(day) {
    const mm = String(viewMonth + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    onChange(`${viewYear}-${mm}-${dd}`);
    setOpen(false);
  }

  const firstDay  = firstDayOfMonth(viewYear, viewMonth);
  const totalDays = daysInMonth(viewYear, viewMonth);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const todayY = today.getFullYear();
  const todayM = today.getMonth();
  const todayD = today.getDate();

  return (
    <div className={`${styles.wrap}${className ? ` ${className}` : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.display}${!value ? ` ${styles.placeholder}` : ''}${error ? ` ${styles.displayError}` : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span>{value ? formatDisplay(value) : placeholder}</span>
        <CalendarDays size={20} className={styles.calendarIcon} />
      </button>

      {open && pos && createPortal(
        <div
          className={styles.popup}
          ref={popupRef}
          style={{ top: pos.top, left: pos.left }}
        >
          <div className={styles.nav}>
            <button type="button" className={styles.navBtn} onClick={prevMonth}>
              <ChevronLeft size={16} />
            </button>
            <span className={styles.monthYear}>{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" className={styles.navBtn} onClick={nextMonth}>
              <ChevronRight size={16} />
            </button>
          </div>

          <div className={styles.grid}>
            {DAY_NAMES.map(n => (
              <span key={n} className={styles.dayName}>{n}</span>
            ))}
            {cells.map((day, i) => {
              if (!day) return <span key={`e${i}`} />;
              const isToday    = viewYear === todayY && viewMonth === todayM && day === todayD;
              const isSelected = parsed && viewYear === parsed.year && viewMonth === parsed.month && day === parsed.day;
              return (
                <button
                  key={day}
                  type="button"
                  className={`${styles.day}${isToday ? ` ${styles.today}` : ''}${isSelected ? ` ${styles.selected}` : ''}`}
                  onClick={() => selectDay(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
